import React, { useState } from 'react';
import axios from 'axios';
import {
  Box, Button, IconButton, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Typography, Chip,
  ToggleButton, ToggleButtonGroup, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useEditMode } from './EditModeContext';
import {
  EMPTY_STRUCTURED_STATE,
  STRUCTURED_TYPES,
  StructuredAssessmentFields,
  buildStructuredInputs,
  hasAnyStructuredInput,
} from './webPresenceAssessmentShared';

const WebPresenceAssessmentEditor = ({ item, webPresence, onUpdate }) => {
  const { isEditMode, addChangeDescription, verificationId } = useEditMode();
  const [manageOpen, setManageOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [newAssessment, setNewAssessment] = useState({ ui_string: '', type: 'neg' });
  const [structuredState, setStructuredState] = useState(EMPTY_STRUCTURED_STATE);
  const [mode, setMode] = useState('structured'); // 'structured' | 'custom'
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!isEditMode || !onUpdate) return null;

  const assessments = item.assessments || [];
  const isStructuredType = STRUCTURED_TYPES.has(item.type);

  const updateItemAssessments = (newAssessments) => {
    const updated = webPresence.map(wp =>
      wp.id === item.id ? { ...wp, assessments: newAssessments } : wp
    );
    onUpdate(updated);
  };

  const resetAddDialog = () => {
    setAddOpen(false);
    setNewAssessment({ ui_string: '', type: 'neg' });
    setStructuredState(EMPTY_STRUCTURED_STATE);
    setMode(isStructuredType ? 'structured' : 'custom');
    setSubmitting(false);
    setError(null);
  };

  const openAddDialog = () => {
    setMode(isStructuredType ? 'structured' : 'custom');
    setAddOpen(true);
  };

  const handleRemove = (idx) => {
    const removed = assessments[idx];
    updateItemAssessments(assessments.filter((_, i) => i !== idx));
    addChangeDescription(`Removed assessment "${removed.ui_string}" from ${item.type} ${item.value}`);
  };

  // Replace-by-name: drop any existing assessments whose `name` matches one of the
  // newly-built ones, then prepend the new ones.
  const mergeReplacingByName = (existing, incoming) => {
    if (!incoming.length) return existing;
    const incomingNames = new Set(incoming.map((a) => a.name));
    const kept = existing.filter((a) => !incomingNames.has(a.name));
    return [...incoming, ...kept];
  };

  const handleAddStructured = async () => {
    const inputs = buildStructuredInputs(item.type, structuredState);
    if (!hasAnyStructuredInput(inputs)) return;

    setSubmitting(true);
    setError(null);
    let built = [];
    try {
      const response = await axios.post(
        `/ui/${verificationId}/web-presence/auto-assessments`,
        { type: item.type, node_id: item.id, inputs },
      );
      built = response.data?.assessments || [];
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to build assessments');
      setSubmitting(false);
      return;
    }

    if (!built.length) {
      setError('No assessments were produced for the selected inputs.');
      setSubmitting(false);
      return;
    }

    const replacedNames = built
      .map((b) => b.name)
      .filter((name) => assessments.some((a) => a.name === name));
    const merged = mergeReplacingByName(assessments, built);
    updateItemAssessments(merged);

    const verb = replacedNames.length ? 'Replaced' : 'Added';
    addChangeDescription(
      `${verb} ${built.length} assessment${built.length === 1 ? '' : 's'} on ${item.type} ${item.value}`,
    );
    resetAddDialog();
  };

  const handleAddCustom = () => {
    if (submitting || !newAssessment.ui_string.trim()) return;
    const scoreMap = { pos: 1.1, neg: 0.8, neutral: 1.0 };
    const signMap = { pos: 'Positive', neg: 'Negative', neutral: 'Neutral' };
    const entry = {
      name: newAssessment.ui_string.split(' - ')[0]?.replace(/\s+/g, '') || 'ManualAssessment',
      ui_string: newAssessment.ui_string,
      score: scoreMap[newAssessment.type] ?? 1.0,
      node_ids: [item.id],
      result_sign: signMap[newAssessment.type] ?? 'Neutral',
      result_enum: `ResultEnum.MANUAL_${Date.now()}`,
    };
    updateItemAssessments([entry, ...assessments]);
    addChangeDescription(`Added assessment "${newAssessment.ui_string}" to ${item.type} ${item.value}`);
    resetAddDialog();
  };

  const handleEditSave = () => {
    if (editIdx === null || !editValue.trim()) return;
    const updated = assessments.map((a, i) =>
      i === editIdx ? { ...a, ui_string: editValue } : a
    );
    updateItemAssessments(updated);
    addChangeDescription(`Edited assessment to "${editValue}" on ${item.type} ${item.value}`);
    setEditIdx(null);
    setEditValue('');
  };

  const structuredInputs = buildStructuredInputs(item.type, structuredState);
  const canSubmitStructured = hasAnyStructuredInput(structuredInputs);
  const canSubmitCustom = !!newAssessment.ui_string.trim();

  return (
    <>
      <Button
        size="small"
        startIcon={<EditIcon />}
        variant="outlined"
        onClick={() => setManageOpen(true)}
        sx={{ borderColor: '#4566EB', color: '#4566EB', mt: 0.5 }}
      >
        Assessments ({assessments.length})
      </Button>

      <Dialog open={manageOpen} onClose={() => setManageOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Assessments for {item.type} — {item.value}
            <Button size="small" startIcon={<AddIcon />} onClick={openAddDialog} sx={{ color: '#4566EB' }}>
              Add
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {assessments.length === 0 && (
            <Typography variant="body2" color="text.secondary">No assessments</Typography>
          )}
          {assessments.map((a, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', py: 0.5, '&:hover': { backgroundColor: '#f5f5f5' } }}>
              <Chip
                label={a.result_sign === 'Positive' ? 'Risk' : a.result_sign === 'Neutral' ? 'Neutral' : 'Mitigator'}
                size="small"
                sx={{
                  mr: 1,
                  backgroundColor: a.result_sign === 'Positive' ? '#ffebee' : a.result_sign === 'Neutral' ? '#f5f5f5' : '#e8f5e9',
                  color: a.result_sign === 'Positive' ? '#d32f2f' : a.result_sign === 'Neutral' ? '#666' : '#2e7d32',
                  fontSize: 11,
                  minWidth: 65,
                }}
              />
              {editIdx === i ? (
                <TextField
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  size="small"
                  fullWidth
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleEditSave();
                    if (e.key === 'Escape') setEditIdx(null);
                  }}
                  sx={{ mr: 1 }}
                />
              ) : (
                <Typography
                  variant="body2"
                  sx={{ flex: 1, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => { setEditIdx(i); setEditValue(a.ui_string); }}
                >
                  {a.ui_string}
                </Typography>
              )}
              {editIdx === i ? (
                <Button size="small" onClick={handleEditSave}>Save</Button>
              ) : (
                <IconButton size="small" onClick={() => handleRemove(i)} sx={{ color: '#d32f2f' }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addOpen} onClose={resetAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add assessment</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {isStructuredType && (
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_, val) => val && setMode(val)}
              size="small"
              disabled={submitting}
            >
              <ToggleButton value="structured">{item.type} assessment</ToggleButton>
              <ToggleButton value="custom">Custom</ToggleButton>
            </ToggleButtonGroup>
          )}

          {mode === 'structured' && isStructuredType && (
            <>
              <Divider textAlign="left">
                <Typography variant="caption">{item.type} assessments</Typography>
              </Divider>
              <StructuredAssessmentFields
                type={item.type}
                state={structuredState}
                onChange={setStructuredState}
              />
              <Typography variant="caption" color="text.secondary">
                Selecting a value replaces any existing assessment of the same type.
              </Typography>
            </>
          )}

          {mode === 'custom' && (
            <>
              <ToggleButtonGroup
                value={newAssessment.type}
                exclusive
                onChange={(_, val) => val && setNewAssessment(prev => ({ ...prev, type: val }))}
                size="small"
              >
                <ToggleButton value="pos" sx={{ color: '#d32f2f' }}>Risk warning</ToggleButton>
                <ToggleButton value="neg" sx={{ color: '#2e7d32' }}>Mitigator</ToggleButton>
                <ToggleButton value="neutral" sx={{ color: '#666' }}>Neutral</ToggleButton>
              </ToggleButtonGroup>
              <TextField
                label="Assessment text"
                placeholder="e.g. LinkedIn company page found with 1000-4999 followers"
                value={newAssessment.ui_string}
                onChange={(e) => setNewAssessment(prev => ({ ...prev, ui_string: e.target.value }))}
                fullWidth
                multiline
                rows={2}
              />
            </>
          )}

          {error && <Typography color="error" variant="caption">{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAddDialog} disabled={submitting}>Cancel</Button>
          {mode === 'structured' && isStructuredType ? (
            <Button
              variant="contained"
              onClick={handleAddStructured}
              disabled={!canSubmitStructured || submitting}
            >
              {submitting ? 'Adding…' : 'Add'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleAddCustom}
              disabled={!canSubmitCustom || submitting}
            >
              Add
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WebPresenceAssessmentEditor;
